import { FlexRow, LabeledData, MaterialIcon, useMoment } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LabelMedium, ParagraphSmall, ParagraphXSmall } from 'baseui/typography';
import { useSetRecoilState } from 'recoil';
import { EVALUATION_STATUS, ON_ROUTE_STATUS } from '../../constants/evaluations';
import useEffectOnce from '../../hooks/useEffectOnce';
import { useStyles } from '../../hooks/useStyles';
import { evaluationDistanceState } from '../../storage/evaluations';
import { Evaluation } from '../../types/evaluations';
import { StyledCard } from '../shared/styledCard/StyledCard';
import { StyledCardBodyWrapper } from '../shared/styledCard/StyledCardBodyWrapper';
import { StyledCardDivider } from '../shared/styledCard/StyledCardDivider';
import { StyledCardHeaderWrapper } from '../shared/styledCard/StyledCardHeaderWrapper';
import { StyledTooltip } from '../ui/StyledTooltip';
import { DistancesTable } from './DistancesTable';

type Props = {
  evaluation: Evaluation;
};

export function EvaluationCard({ evaluation }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const { formatDateFromTs } = useMoment();
  const setEvaluationDistance = useSetRecoilState(evaluationDistanceState);

  useEffectOnce(() => {
    return () => {
      setEvaluationDistance(undefined);
    };
  });

  return (
    <StyledCard>
      <StyledCardHeaderWrapper>
        {(evaluation.status || evaluation.created) && (
          <>
            <FlexRow
              gap={theme.sizing.scale300}
              centered
            >
              {evaluation.status && (
                <LabelMedium
                  className={css({
                    paddingRight: theme.sizing.scale400,
                    paddingLeft: theme.sizing.scale400,
                    paddingTop: theme.sizing.scale100,
                    paddingBottom: theme.sizing.scale100,
                    backgroundColor:
                      evaluation.status === ON_ROUTE_STATUS ? theme.colors.positive : theme.colors.negative,
                    color: '#FFF',
                    borderRadius: theme.sizing.scale600,
                  })}
                >
                  {EVALUATION_STATUS[evaluation.status]}
                </LabelMedium>
              )}
              {evaluation.created && (
                <ParagraphSmall
                  color={theme.colors.contentSecondary}
                  margin={0}
                >
                  {formatDateFromTs(evaluation.created)}
                </ParagraphSmall>
              )}
            </FlexRow>
          </>
        )}
        {evaluation.distances.length === 1 && (
          <StyledTooltip
            content="Ver en el mapa"
            showArrow={false}
            placement="bottom"
            ignoreBoundary
          >
            <Button
              shape="circle"
              kind="tertiary"
              size="mini"
              onClick={() => setEvaluationDistance(evaluation.distances[0])}
            >
              <MaterialIcon
                name="location_searching"
                size="mini"
              />
            </Button>
          </StyledTooltip>
        )}
      </StyledCardHeaderWrapper>
      <StyledCardDivider />
      <StyledCardBodyWrapper>
        {evaluation.percentage !== undefined && (
          <span className={css({ marginBottom: theme.sizing.scale500, display: 'block' })}>
            <LabeledData
              label="Porcentaje de separación:"
              content={`${evaluation.percentage.toPrecision(2)}%`}
            />
          </span>
        )}
        {evaluation.distances.length === 1 ? (
          <LabeledData
            label={'Distancia:'}
            content={`${evaluation.distances[0].distance.toFixed(2)} m`}
          />
        ) : (
          <DistancesTable
            data={evaluation.distances.map((distance, distanceIndex) => ({
              items: [`${(+distance.distance.toFixed(2)).toLocaleString()} m`, EVALUATION_STATUS[distance.status]],
              id: distanceIndex.toString(),
              onClick: () => {
                setEvaluationDistance(distance);
              },
            }))}
          />
        )}
        <ParagraphXSmall
          marginBottom={0}
          color={theme.colors.contentTertiary}
        >
          * La distancia de separación permitida para esta evaluación es de: {evaluation.maxDistanceSeparation ?? 0} m
        </ParagraphXSmall>
      </StyledCardBodyWrapper>
    </StyledCard>
  );
}
